<template>
  <div class="flex flex-col gap-5">
    <BaseSection heading="Geräteservice-Bericht" :is-main-heading="true">
      <div class="flex flex-col">
        <BaseFormInput
          v-model="TKDServiceModel.ticketNumber"
          class="md:basis-full"
          label="Ticketnummer"
          :optional="false"
          test-id="ticketNumber"
          :validation="v$.TKDService.ticketNumber"
        >
        </BaseFormInput>
        <div v-if="suggestedTicketNumbers.length > 0" class="mb-2">
          <div class="text-xs flex items-center gap-1 my-2">
            <div class="flex flex-wrap gap-2">
              Wählen Sie eine Ticketnummer.
              <span v-if="oldDeviceSerialNumber"
                >Betroffenes Gerät: S/N {{ oldDeviceSerialNumber }}
              </span>
            </div>
          </div>
          <div class="items-start flex flex-wrap gap-2">
            <BaseChip
              v-for="suggestedNumber in suggestedTicketNumbers"
              :key="suggestedNumber.ticketNumber"
              :text="suggestedNumber.ticketNumber"
              :outlined="false"
              :type="
                suggestedNumber.ticketNumber === TKDServiceModel.ticketNumber
                  ? 'activeInfo'
                  : 'info'
              "
              class="text-sm px-3 py-2 border-none"
              @click="setTicketNumber(suggestedNumber)"
            />
          </div>
        </div>
        <BaseFormInput
          v-model="TKDServiceModel.errorDescription"
          class="md:basis-full"
          label="Fehlerbeschreibung"
          placeholder="Fehlerursache: Bitte beschreibe die Fehlerursache genauer."
          test-id="errorDescription"
          :validation="v$.TKDService.errorDescription"
        />
        <Checkbox
          v-if="TKDServiceModel"
          v-model:checked="TKDServiceModel.thirdPartyFault"
          test-id="third-party-fault"
          label="Fremdverschulden"
          class="my-2"
        />
        <BaseInformation
          v-if="TKDServiceModel?.thirdPartyFault"
          status="warning"
          data-testid="third-party-fault-consequence"
          class="mb-2"
          information-text="Bei Fremdverschulden wird den Kunden die Serviceleistung in Rechnung gestellt."
        />
        <BaseTextarea
          v-model:input-text="TKDServiceModel.report"
          test-id="serviceReport"
          placeholder="Servicebericht: Trage die durchgeführten Arbeitsmaßnahmen ein."
          label="Service Bericht"
          :validation="v$.TKDService.report"
        />
      </div>
    </BaseSection>
    <BaseSection heading="Foto" :is-main-heading="true" class="mt-5">
      <ImageCaptureRef
        v-model:images="images"
        :object-id="installationPoint.id"
        :object-type="ImageObjectType.DEVICE_VERIFICATION"
        class="mt-5"
      />
      <Checkbox
        v-model:checked="isImageNotAllowedChecked"
        test-id="resident-denied-photo"
        label="Nutzer verweigert Foto"
        class="mt-5"
      />
    </BaseSection>
  </div>
</template>
<script setup lang="ts">
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useValidation } from '@/composables/useValidation';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import BaseFormInput from '@/components/UI/Input/BaseFormInput.vue';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import BaseChip from '@/components/UI/Chip/BaseChip.vue';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import ImageCaptureRef from '@/components/Forms/DeviceFormComponents/ImageCaptureRef.vue';

import { Image, ImageObjectType } from '@/models/Image';
import type { TKDService } from '@/models/installationPoint/TKDService';
import type { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { ANALYTICS_PROVIDER } from '@/plugins/bxAnalytics';

const props = defineProps<{
  oldDeviceSerialNumber: string;
  suggestedTicketNumbers: Array<Record<string, string>>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const TKDServiceModel = defineModel<TKDService>('tkdService');
const installationPoint = defineModel<InstallationPoint>('installationPoint');
const images = defineModel<Image[]>('images');

const isImageNotAllowedChecked = ref(false);

const { v$ } = useValidation({ TKDService: TKDServiceModel });

onMounted(() => {
  autoSelectTicketNumber();
});

const analytics = inject(ANALYTICS_PROVIDER);

const setTicketNumber = (suggestedNumber: any) => {
  if (!TKDServiceModel.value) return;
  TKDServiceModel.value.ticketNumber = suggestedNumber.ticketNumber;
  analytics?.captureEvent('select_ticketNumber');
};

const autoSelectTicketNumber = () => {
  if (TKDServiceModel.value && !TKDServiceModel.value.ticketNumber) {
    const match = props.suggestedTicketNumbers.find(
      (t) => t.deviceSerialNumber === props.oldDeviceSerialNumber
    );
    if (match) {
      TKDServiceModel.value.ticketNumber = match.ticketNumber;
    }
  }
};

const isLocalFormValid = computed(() => {
  return !v$.value.$invalid;
});

watch(isLocalFormValid, (val) => emit('update:modelValue', val));
</script>
